import React, {useCallback, useState, useEffect, useRef} from 'react';
import {Card, Page, Layout, FormLayout, Banner, Spinner, Checkbox, TextField, Form, Frame, Badge, Button, Toast, Tabs, Collapsible, Link} from '@shopify/polaris';
import createApp from '@shopify/app-bridge';
import {Redirect} from '@shopify/app-bridge/actions';
import {getSessionToken} from "@shopify/app-bridge-utils";
import ReactHtmlParser from 'react-html-parser';
import { Editor } from "@tinymce/tinymce-react";

function Home() {
    const [showBanner, setShowBanner] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [subscriptionText, setSubscriptionText] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [replyEnabled, setReplyEnabled] = useState(false);
    const [replySubject, setReplySubject] = useState(null);
    const [replySenderName, setReplySenderName] = useState(null);
    const [replyToEmail, setReplyToEmail] = useState(null);
    const [replyCC, setReplyCC] = useState(null);
    const [replyMessage, setReplyMessage] = useState(null);
    const [pageJustLoadedReplySubject, setPageJustLoadedReplySubject] = useState(true);
    const [pageJustLoadedReplySenderName, setPageJustLoadedReplySenderName] = useState(true);
    const [pageJustLoadedReplyToEmail, setPageJustLoadedReplyToEmail] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState('Error While Saving Settings');
    const [selectedTab, setSelectedTab] = useState(0);
    const [mailCount, setMailCount] = useState(0);

    const [newsletterReplyEnabled, setNewsletterReplyEnabled] = useState(false);
    const [newsletterReplySubject, setNewsletterReplySubject] = useState(null);
    const [newsletterReplySenderName, setNewsletterReplySenderName] = useState(null);
    const [newsletterReplyToEmail, setNewsletterReplyToEmail] = useState(null);
    const [newsletterReplyCC, setNewsletterReplyCC] = useState(null);
    const [newsletterReplyMessage, setNewsletterReplyMessage] = useState(null);
    const [pageJustLoadedNewsletterReplySubject, setPageJustLoadedNewsletterReplySubject] = useState(true);
    const [pageJustLoadedNewsletterReplySenderName, setPageJustLoadedNewsletterReplySenderName] = useState(true);
    const [pageJustLoadedNewsletterReplyToEmail, setPageJustLoadedNewsletterReplyToEmail] = useState(true);

    const [config, setConfig] = useState(null);

    const [demoOpen, setDemoOpen] = useState(false);

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        const query = new URLSearchParams(window.location.search);
        const configApp = {
            apiKey: window['apiKey'],
            host: query.get('host'),
            forceRedirect: true
        };
        setConfig(configApp);
        window['bridge-app'] = createApp(configApp);

        const sessionToken = await getSessionToken(window['bridge-app']);
        if (sessionToken) {
            const settings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionToken
                }
            };

            const response = await fetch('https://contactautoreply.com/api/subscription/current/' + window.$queryString, settings);

            // if user session expired reload the page
            if (response.status === 403) {
                window.location.reload();
                return false;
            }
            // if shop is banned we just show an alert
            if (response.status === 404) {
                alert('Account disabled for suspicious activity!')
                return false;
            }

            const responseJson = await response.json();

            if (response.status === 274) {
                window.location.href = 'https://contactautoreply.com/login/' + window.$queryString;
                return false;
            }
            if (response.status === 275) {
                const redirect = Redirect.create(window['bridge-app']);
                redirect.dispatch(Redirect.Action.REMOTE, responseJson.redirect_url);
                return false;
            }

            const subscription = responseJson;

            setSubscription(subscription);
            setReplyEnabled(subscription.reply_enabled);
            setReplySubject(subscription.reply_subject);
            setReplySenderName(subscription.reply_sender_name);
            setReplyToEmail(subscription.reply_to_email);
            setReplyCC(subscription.reply_cc);
            setReplyMessage(subscription.reply_message);
            setShowBanner(!subscription.hide_banner);
            setNewsletterReplyEnabled(subscription.newsletter_reply_enabled);
            setNewsletterReplySubject(subscription.newsletter_reply_subject);
            setNewsletterReplySenderName(subscription.newsletter_reply_sender_name);
            setNewsletterReplyToEmail(subscription.newsletter_reply_to_email);
            setNewsletterReplyCC(subscription.newsletter_reply_cc);
            setNewsletterReplyMessage(subscription.newsletter_reply_message);
            setMailCount(subscription.mail_count);

            if (subscription.is_premium) {
                if (subscription.subscription === 1 || subscription.subscription === 3) {
                    setSubscriptionText('Basic');
                } else {
                    setSubscriptionText('Premium');
                }
                setSubscriptionStatus(1);
            } else {
                setSubscriptionText('Your subscription charge is invalid. Please contact support if you have any questions');
                setSubscriptionStatus(0);
                setReplyEnabled(false);
                setNewsletterReplyEnabled(false);
            }
        }
    };

    const setBannerHidden = () => {
        getSessionToken(window['bridge-app'])
            .then((sessionToken) => {
                const settings = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + sessionToken
                    }
                };
                fetch(`https://contactautoreply.com/api/subscription/hidebanner/${window.$queryString}`, settings)
                    .then(response => {
                        // if user session expired reload the page
                        if (response.status === 403) {
                            window.location.reload();
                            return false;
                        }

                        setShowBanner(false);
                    });
            })
            .catch((err) => console.log(err));
    };

    const handleFormSubmit = () => {
        setPageJustLoadedReplySubject(false);
        setPageJustLoadedReplySenderName(false);
        setPageJustLoadedReplyToEmail(false);

        if (
            replySubject && replySenderName && replyToEmail
        ) {
            setSubmitLoading(true);

            subscription.reply_enabled = replyEnabled;
            subscription.reply_subject = replySubject;
            subscription.reply_sender_name = replySenderName;
            subscription.reply_to_email = replyToEmail;
            subscription.reply_cc = replyCC;
            subscription.reply_message = replyMessage;
            // console.log(subscription);

            getSessionToken(window['bridge-app'])
                .then((sessionToken) => {
                    const settings = {
                        method: 'POST',
                        body: JSON.stringify(subscription),
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + sessionToken
                        }
                    };
                    fetch(`https://contactautoreply.com/api/subscription/save/${window.$queryString}`, settings)
                        .then(response => {
                            if (response.status === 200) {
                                setSubmitSuccess(true);
                            } else if (response.status === 403) {
                                window.location.reload();
                                return false;
                            } else if (response.status === 405) {
                                setSubmitErrorMessage('<span>Your current subscription charge is not active so you are not able to activate the auto reply.</span>');
                                setSubmitError(true);
                                setReplyEnabled(false);
                            } else {
                                setSubmitError(true);
                            }
                            return response.json();
                        }).then(response => {
                            setSubmitLoading(false);
                        }).catch(err => {
                            setSubmitError(true);
                            setSubmitLoading(false);
                        });
                });
        }
    };

    const handleNewsletterFormSubmit = () => {
        setPageJustLoadedNewsletterReplySubject(false);
        setPageJustLoadedNewsletterReplySenderName(false);
        setPageJustLoadedNewsletterReplyToEmail(false);

        if (
            newsletterReplySubject && newsletterReplySenderName && newsletterReplyToEmail
        ) {
            setSubmitLoading(true);

            subscription.newsletter_reply_enabled = newsletterReplyEnabled;
            subscription.newsletter_reply_subject = newsletterReplySubject;
            subscription.newsletter_reply_sender_name = newsletterReplySenderName;
            subscription.newsletter_reply_to_email = newsletterReplyToEmail;
            subscription.newsletter_reply_cc = newsletterReplyCC;
            subscription.newsletter_reply_message = newsletterReplyMessage;
            // console.log(subscription);

            getSessionToken(window['bridge-app'])
                .then((sessionToken) => {
                    const settings = {
                        method: 'POST',
                        body: JSON.stringify(subscription),
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + sessionToken
                        }
                    };
                    fetch(`https://contactautoreply.com/api/subscription/savenewsletter/${window.$queryString}`, settings)
                        .then(response => {
                            if (response.status === 200) {
                                setSubmitSuccess(true);
                            } else if (response.status === 403) {
                                window.location.reload();
                                return false;
                            } else if (response.status === 405) {
                                setSubmitErrorMessage('<span>Your current subscription charge is not active so you are not able to activate the auto reply.</span>');
                                setSubmitError(true);
                                setNewsletterReplyEnabled(false);
                            } else {
                                setSubmitError(true);
                            }
                            return response.json();
                        }).then(response => {
                        setSubmitLoading(false);
                    }).catch(err => {
                        setSubmitError(true);
                        setSubmitLoading(false);
                    });
                });
        }
    };

    const handleReplyEnabledChange = useCallback((value) => {
        setReplyEnabled(value);
    }, []);

    const handleReplySubjectChange = useCallback((value) => {
        // Remove the "@" symbol from the value
        const newValue = value.replace('@', '');

        setReplySubject(newValue);
    }, []);

    const handleReplySenderNameChange = useCallback((value) => {
        // Remove the "@" symbol from the value
        const newValue = value.replace('@', '');

        setReplySenderName(newValue);
    }, []);

    const handleReplyToEmailChange = useCallback((value) => {
        setReplyToEmail(value);
    }, []);

    const handleReplyCC = useCallback((value) => {
        setReplyCC(value);
    }, []);

    const handleReplyMessage = useCallback((value) => {
        setReplyMessage(value);
    }, []);


    const handleNewsletterReplyEnabledChange = useCallback((value) => {
        setNewsletterReplyEnabled(value);
    }, []);

    const handleNewsletterReplySubjectChange = useCallback((value) => {
        // Remove the "@" symbol from the value
        const newValue = value.replace('@', '');

        setNewsletterReplySubject(newValue);
    }, []);

    const handleNewsletterReplySenderNameChange = useCallback((value) => {
        // Remove the "@" symbol from the value
        const newValue = value.replace('@', '');

        setNewsletterReplySenderName(newValue);
    }, []);

    const handleNewsletterReplyToEmailChange = useCallback((value) => {
        setNewsletterReplyToEmail(value);
    }, []);

    const handleNewsletterReplyCC = useCallback((value) => {
        setNewsletterReplyCC(value);
    }, []);

    const handleNewsletterReplyMessage = useCallback((value) => {
        setNewsletterReplyMessage(value);
    }, []);

    const toggleSubmitSuccess = useCallback(() => {
        setSubmitSuccess(false);
    }, []);
    const toggleSubmitError = useCallback(() => {
        setSubmitError(false);
    }, []);

    const hideBanner = () => {
        setShowBanner(false);
        setBannerHidden();
    };

    const handleToggleDemo = () => {
		setDemoOpen((open) => !open);
    };

    const tabs = [
        {
            id: 'contact-form',
            content: 'Contact Form (Built-in)',
            fullContent: <Form autoComplete={false} onSubmit={handleFormSubmit}>
                <Frame>
                    <FormLayout>
                        <Checkbox
                            label="Automatic Reply Email Enabled for Contact Form?"
                            checked={replyEnabled}
                            onChange={handleReplyEnabledChange}
                        />
                        <TextField label="Subject" value={replySubject}
                            onChange={handleReplySubjectChange}
                            error={(replySubject || pageJustLoadedReplySubject) ? null : "Subject is required"}
                        />
                        <TextField label="Sender Name" value={replySenderName}
                            onChange={handleReplySenderNameChange}
                            error={(replySenderName || pageJustLoadedReplySenderName) ? null : "Sender Name is required"}
                        />
                        <TextField label="Reply To Email" type="email" value={replyToEmail}
                            onChange={handleReplyToEmailChange}
                            error={(replyToEmail || pageJustLoadedReplyToEmail) ? null : "Sender Email is required"}
                            helpText="If the client wants to reply to the reply email he gets, this is the email the reply will go to."
                        />
                        <TextField label="CC - Carbon Copy" value={replyCC}
                            onChange={handleReplyCC}
                            helpText="Provide a list of emails separated by ','"
                        />
                        <div className="Polaris-Label"
                            style={{
                               marginBottom: '-1rem',
                            }}
                        >
                            <label className="Polaris-Label__Text">Message - HTML Format</label>
                        </div>
                        <Editor
                            apiKey="a6ggv9vsul6uq815qyoqpq02s1xf0cos3uj755vdpsc5hn16"
                            title="Message"
                            value={replyMessage}
                            init={{
                              height: 450,
                              plugins: [
                                'advlist autolink lists link image charmap print preview anchor',
                                'searchreplace visualblocks code fullscreen',
                                'insertdatetime media table paste imagetools wordcount'
                              ],
                              toolbar:
                                'code | insertfile undo redo | styleselect | bold italic | ' +
                                  'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                                  'link image',
                              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                              extended_valid_elements:"style,link[href|rel]",
                              custom_elements:"style,link,~link"
                            }}
                            onEditorChange={handleReplyMessage}
                        />
                        <div className="Polaris-Labelled__HelpText"
                            style={{
                               marginTop: '-1rem',
                            }}
                        >
                            The Message Content needs to have email friendly format for it to
                            be properly rendered in the reply email and to make sure that it won't be flagged as spam.
                            <br/>
                            <b>Available variables</b>:
                            <br/>
                            &nbsp;&nbsp;{"{{client_name}}"} - name of the client who submitted the contact form
                            <br/>
                            &nbsp;&nbsp;{"{{client_email}}"} - email of the client who submitted the contact form
                            <br/>
                            &nbsp;&nbsp;{"{{client_phone}}"} - phone number of the client who submitted the contact form
                            <br/>
                            &nbsp;&nbsp;{"{{client_message}}"} - message submitted by the client in contact form
                        </div>
                    </FormLayout>
                    <br/>
                    {
                        submitLoading ?
                            <div className="main-button-wrapper">
                                <Button submit primary loading>Save</Button>
                            </div>
                            :
                            <div className="main-button-wrapper">
                                <Button submit primary>Save</Button>
                            </div>
                    }
                    {
                        submitSuccess ?
                            <Toast content="Settings Saved" onDismiss={toggleSubmitSuccess} />
                            :
                            null
                    }
                    {
                        submitError ?
                            <Toast content={ReactHtmlParser (submitErrorMessage)} error onDismiss={toggleSubmitError} />
                            :
                            null
                    }
                </Frame>
            </Form>,
            panelID: 'built-in-contact-form',
        },
        {
            id: 'newsletter-form',
            content: 'Newsletter Form (Built-in)',
            fullContent: <Form autoComplete={false} onSubmit={handleNewsletterFormSubmit}>
                <Frame>
                    <FormLayout>
                        <Checkbox
                            label="Automatic Reply Email Enabled for Newsletter?"
                            checked={newsletterReplyEnabled}
                            onChange={handleNewsletterReplyEnabledChange}
                        />
                        <TextField label="Subject" value={newsletterReplySubject}
                            onChange={handleNewsletterReplySubjectChange}
                            error={(newsletterReplySubject || pageJustLoadedNewsletterReplySubject) ? null : "Subject is required"}
                        />
                        <TextField label="Sender Name" value={newsletterReplySenderName}
                            onChange={handleNewsletterReplySenderNameChange}
                            error={(newsletterReplySenderName || pageJustLoadedNewsletterReplySenderName) ? null : "Sender Name is required"}
                        />
                        <TextField label="Reply To Email" type="email" value={newsletterReplyToEmail}
                            onChange={handleNewsletterReplyToEmailChange}
                            error={(newsletterReplyToEmail || pageJustLoadedNewsletterReplyToEmail) ? null : "Sender Email is required"}
                            helpText="If the client wants to reply to the reply email he gets, this is the email the reply will go to."
                        />
                        <TextField label="CC - Carbon Copy" value={newsletterReplyCC}
                            onChange={handleNewsletterReplyCC}
                            helpText="Provide a list of emails separated by ','"
                        />
                        <div className="Polaris-Label"
                            style={{
                               marginBottom: '-1rem',
                            }}
                        >
                            <label className="Polaris-Label__Text">Message - HTML Format</label>
                        </div>
                        <Editor
                            apiKey="a6ggv9vsul6uq815qyoqpq02s1xf0cos3uj755vdpsc5hn16"
                            title="Message"
                            value={newsletterReplyMessage}
                            init={{
                              height: 300,
                              menubar: false,
                              plugins: [
                                'advlist autolink lists link image',
                                'charmap print preview anchor help',
                                'searchreplace visualblocks code',
                                'insertdatetime media table paste wordcount'
                              ],
                              toolbar:
                                'code | undo redo | formatselect | bold italic | \
                                alignleft aligncenter alignright | \
                                bullist numlist outdent indent | help',
                              extended_valid_elements:"style,link[href|rel]",
                              custom_elements:"style,link,~link"
                            }}
                            onEditorChange={handleNewsletterReplyMessage}
                        />
                        <div className="Polaris-Labelled__HelpText"
                            style={{
                               marginTop: '-1rem',
                            }}
                        >
                            The Message Content needs to have email friendly format for it to
                            be properly rendered in the reply email and to make sure that it won't be flagged as spam.
                            <br/>
                            <b>Available variables</b>:
                            <br/>
                            &nbsp;&nbsp;{"{{client_email}}"} - email of the client who submitted the contact form
                        </div>
                    </FormLayout>
                    <br/>
                    {
                        submitLoading ?
                            <div className="main-button-wrapper">
                                <Button submit primary loading>Save</Button>
                            </div>
                            :
                            <div className="main-button-wrapper">
                                <Button submit primary>Save</Button>
                            </div>
                    }
                    {
                        submitSuccess ?
                            <Toast content="Settings Saved" onDismiss={toggleSubmitSuccess} />
                            :
                            null
                    }
                    {
                        submitError ?
                            <Toast content={ReactHtmlParser (submitErrorMessage)} error onDismiss={toggleSubmitError} />
                            :
                            null
                    }
                </Frame>
            </Form>,
            panelID: 'built-in-newsletter-form',
        },
    ];

    const handleTabChange = useCallback(
    (selectedTabIndex) => setSelectedTab(selectedTabIndex),
    [],
    );

    return (
        <Page>
            <Layout>
                <Layout.Section>
                    {
                        subscription ?
                            [
                                showBanner ?
                                        <Banner
                                            title="About the app"
                                            status="info"
                                            onDismiss={hideBanner}
                                        >This app will only work with the Shopify built-in Contact and Newsletter Forms.
                                            If you have any problems with configuring the app or the contact form please write us by clicking the "Support" button.<br/>
                                            For a detailed tutorial on how to create a Contact Us Page read this post
                                             <a
                                                 style={{
                                                       display: 'inline-block',
                                                       marginLeft: '5px',
                                                 }}
                                                 href="https://help.shopify.com/en/manual/online-store/os/using-themes/change-the-layout/add-contact-form" target="_blank">
                                                 Add Contact Us Page
                                             </a>
                                        </Banner>
                                        :
                                        null,
                                <br/>,
                                <Card title="Subscription" sectioned>
                                    <table className="account-status">
                                        <tbody>
                                            <tr>
                                                <td>Current Plan:</td>
                                                {
                                                    subscriptionStatus === 1 ?
                                                        <td>
                                                            <Badge status="success" size="medium" progress="complete">{subscriptionText}</Badge>
                                                        </td>
                                                        :
                                                        <td>
                                                            <Badge status="critical" size="medium" progress="complete">{subscriptionText}</Badge>
                                                        </td>
                                                }
                                                <td>
                                                    <Link url={`https://contactautoreply.com/subscription${window.$queryString}`}>Change Plan</Link>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Emails Used this Month:</td>
                                                {
                                                    subscription.subscription === 2 || subscription.subscription === 4 ?
                                                        mailCount >= 3500 ?
                                                            <td>
                                                                <Badge status="attention" size="medium" progress="complete">{mailCount} / 4000</Badge>
                                                            </td>
                                                            :
                                                            <td>
                                                                <Badge status="success" size="medium" progress="complete">{mailCount} / 4000</Badge>
                                                            </td>
                                                        :
                                                        mailCount >= 650 ?
                                                            <td>
                                                                <Badge status="attention" size="medium" progress="complete">{mailCount} / 1000</Badge>
                                                            </td>
                                                            :
                                                            <td>
                                                                <Badge status="success" size="medium" progress="complete">{mailCount} / 1000</Badge>
                                                            </td>
                                                }
                                                <td></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    {
                                        subscription.id > 810 ?
                                            <div className="app-status-wrapper" style={{fontSize: '1.4rem', fontWeight: '600'}}>
                                                <br/>
                                                <span style={{ marginRight: '20px' }}>
                                                To Enable the app click this button
                                                </span>
                                                <a
                                                    className="Polaris-Button Polaris-Button--primary Polaris-Button--sizeSlim"
                                                    onClick={handleFormSubmit}
                                                    href={`https://${subscription.myshopify_domain}/admin/themes/current/editor?context=apps&activateAppId=32a94abe-3d7a-4526-920a-d901f786f4d9/app-embed`}
                                                    data-polaris-unstyled="true"
                                                    target="_blank"
                                                >
                                                    <span className="Polaris-Button__Content">
                                                        <span className="Polaris-Button__Text">Enable App</span>
                                                    </span>
                                                </a>
                                                <br/>
                                                <Button
                                                    onClick={handleToggleDemo}
                                                    ariaControls="basic-collapsible"
                                                >
                                                    View App Enable Demo
                                                </Button>
                                                <Collapsible
                                                    open={demoOpen}
                                                    id="demo-wrapper"
                                                    transition={{duration: '500ms', timingFunction: 'ease-in-out'}}
                                                >
                                                    <br/>
                                                    <img src="https://contactautoreply.com/media/activate-app.gif" alt="Activate app tutorial" style={{ maxWidth: '100%' }} />
                                                </Collapsible>
                                            </div>
                                            :
                                            null
                                    }
                                </Card>,
                                <br/>,
                                <Card>
                                    <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                                        <Card.Section>
                                            {tabs[selectedTab].fullContent}
                                        </Card.Section>
                                    </Tabs>
                                </Card>
                            ]
                            :
                            <Card title="Contact Form AutoResponder Settings" sectioned>
                                <Spinner accessibilityLabel="Spinner example" size="large" color="teal" />
                            </Card>
                    }
                </Layout.Section>
            </Layout>
        </Page>
    );
}

export default Home;
